import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Parameter definitions
const parameterDefinitions = [
  { key: "moduleSerialNumber", label: "Module Serial Number" },
  { key: "goldenModuleReport", label: "Golden Module Report" },
  { key: "alphaValue", label: "Alpha Value" },
  { key: "betaValue", label: "Beta Value" },
  { key: "gammaValue", label: "Gamma Value" },
  { key: "measuredPmax", label: "Measured: Pmax (W)" },
  { key: "measuredISC", label: "Measured: Isc (A)" },
  { key: "measuredVOC", label: "Measured: Voc (V)" },
  { key: "measuredIMP", label: "Measured: Imp (A)" },
  { key: "measuredVMP", label: "Measured: Vmp (V)" },
  { key: "measuredFF", label: "Measured: FF (%)" },
  { key: "nominalPmax", label: "Nominal: Pmax (W)" },
  { key: "nominalISC", label: "Nominal: Isc (A)" },
  { key: "nominalVOC", label: "Nominal: Voc (V)" },
  { key: "nominalIMP", label: "Nominal: Imp (A)" },
  { key: "nominalVMP", label: "Nominal: Vmp (V)" },
  { key: "nominalFF", label: "Nominal: FF (%)" },
];

const PrimaryModuleCalibrationTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("PrimaryModuleCalibrationTable props:", { data, startDate, endDate, shifts, subForm });

  // Define static table headers
  const staticHeaders = [
    { id: "parameter", label: "Parameter" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Collect all lines from data
  const allLines = useMemo(() => {
    const lines = new Set();
    data.forEach((item) => {
      Object.keys(item.form_data?.lines || {}).forEach((line) => lines.add(line));
    });
    return [...lines].sort();
  }, [data]);

  // Create dynamic headers
  const dynamicHeaders = useMemo(() => {
    const headers = [];
    dates.forEach((date) => {
      if (!startDate || !endDate || (date >= formatDate(startDate) && date <= formatDate(endDate))) {
        effectiveShifts.forEach((shift) => {
          allLines.forEach((line) => {
            headers.push({
              id: `${date}_${shift}_${line}`,
              label: `${shift} ${line}`,
              date,
              shift,
              line,
            });
          });
        });
      }
    });
    return headers;
  }, [dates, effectiveShifts, allLines, startDate, endDate]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: estimateTextWidth("Parameter"),
      value: 100, // Default width for value columns
    };

    // Width for parameter column
    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.min(
        Math.max(widths.parameter, estimateTextWidth(param.label)),
        8 * window.innerWidth / 100
      );
    });

    // Width for value columns
    data.forEach((item) => {
      Object.entries(item.form_data?.lines || {}).forEach(([lineKey, lineData]) => {
        widths.value = Math.min(
          Math.max(widths.value, estimateTextWidth(lineKey)),
          8 * window.innerWidth / 100
        );
        Object.values(lineData).forEach((value) => {
          widths.value = Math.min(
            Math.max(widths.value, estimateTextWidth(String(value))),
            8 * window.innerWidth / 100
          );
        });
      });
    });

    dynamicHeaders.forEach((header) => {
      widths[header.id] = widths.value;
    });

    return widths;
  }, [data, dynamicHeaders]);

  // Calculate left position for sticky column
  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = parameterDefinitions.map((param) => ({
      parameter: param.label,
      data: {},
    }));

    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      Object.entries(item.form_data?.lines || {}).forEach(([line, lineData]) => {
        const key = `${date}_${shift}_${line}`;
        parameterDefinitions.forEach((param, index) => {
          rows[index].data[key] = String(lineData[param.key] || "N/A");
        });
      });
    });

    return rows;
  }, [data]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    // Row 1: Dates (merged across shift×line columns)
    const row1 = ["Parameter"];
    let colStart = 1;

    dates.forEach((date) => {
      const colsPerDate = effectiveShifts.length * allLines.length;
      if (colsPerDate > 0) {
        row1.push(date);
        if (colsPerDate > 1) {
          merges.push({
            s: { r: 0, c: colStart },
            e: { r: 0, c: colStart + colsPerDate - 1 },
          });
        }
        colStart += colsPerDate;
      }
    });

    // Row 2: Shift + Line headers
    const row2 = ["Parameter"];
    dynamicHeaders.forEach((h) => row2.push(h.label));

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      dynamicHeaders.forEach((header) => {
        r.push(row.data[header.id] || "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply merged cells for date row
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 28 }, // Parameter column
      ...Array(dynamicHeaders.length).fill({ wch: 20 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Primary Module Cal");

    const fileName = `Primary_Module_Calibration_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Primary (Golden) Module Calibration Record
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dynamicHeaders.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={
                    effectiveShifts.length *
                    allLines.filter(() =>
                      dynamicHeaders.some((h) => h.date === date)
                    ).length
                  }
                  columnWidth={
                    effectiveShifts.length *
                    allLines.filter(() =>
                      dynamicHeaders.some((h) => h.date === date)
                    ).length *
                    columnWidths.value
                  }
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {dynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + dynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {dynamicHeaders.map((header) => (
                    <BodyTableCell
                      key={`${header.id}_${index}`}
                      columnWidth={columnWidths[header.id]}
                    >
                      {row.data[header.id] || "N/A"}
                    </BodyTableCell>
                  ))}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PrimaryModuleCalibrationTable;